import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X, Globe } from 'lucide-react';

const Header = () => {
  const { t, language, toggleLanguage } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navItems = [
    { key: 'home', path: '/' },
    { key: 'games', path: '/#games' },
    { key: 'team', path: '/#team' },
    { key: 'about', path: '/#about' },
    { key: 'contact', path: '/#contact' },
    { key: 'discord', path: '/#discord' }
  ];

  const scrollToSection = (e, path) => {
    e.preventDefault();
    setMobileMenuOpen(false);

    if (path === '/') {
      // Home button - always scroll to top
      if (location.pathname !== '/') {
        window.location.href = '/';
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else if (path.includes('#')) {
      // Section links
      const id = path.split('#')[1];
      if (location.pathname !== '/') {
        window.location.href = path;
      } else {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-sm border-b border-red-900/20">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            onClick={(e) => {
              e.preventDefault();
              if (location.pathname !== '/') {
                window.location.href = '/';
              } else {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
            className="flex items-center space-x-3 group"
          >
            <img
              src="/logo.png"
              alt="Silly Hat Studio"
              className="h-12 w-12 object-contain transition-transform group-hover:scale-110"
            />
            <span className="text-xl font-bold text-red-600 hidden sm:block">
              Silly Hat Studio
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              item.external ? (
                <a
                  key={item.key}
                  href={item.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-red-500 transition-colors text-sm uppercase tracking-wider font-medium"
                >
                  {t(`nav.${item.key}`)}
                </a>
              ) : (
                <Link
                  key={item.key}
                  to={item.path}
                  onClick={(e) => scrollToSection(e, item.path)}
                  className="text-gray-300 hover:text-red-500 transition-colors text-sm uppercase tracking-wider font-medium"
                >
                  {t(`nav.${item.key}`)}
                </Link>
              )
            ))}

            {/* Language Switcher */}
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-red-900/20 hover:bg-red-900/40 text-red-500 transition-colors"
            >
              <Globe size={16} />
              <span className="text-sm font-medium uppercase">{language}</span>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center space-x-4">
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-1 px-2 py-1 rounded bg-red-900/20 text-red-500"
            >
              <Globe size={14} />
              <span className="text-xs uppercase">{language}</span>
            </button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-gray-300 hover:text-red-500 transition-colors"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-red-900/20">
            {navItems.map((item) => (
              item.external ? (
                <a
                  key={item.key}
                  href={item.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-3 text-gray-300 hover:text-red-500 transition-colors uppercase tracking-wider text-sm font-medium"
                >
                  {t(`nav.${item.key}`)}
                </a>
              ) : (
                <Link
                  key={item.key}
                  to={item.path}
                  onClick={(e) => scrollToSection(e, item.path)}
                  className="block py-3 text-gray-300 hover:text-red-500 transition-colors uppercase tracking-wider text-sm font-medium"
                >
                  {t(`nav.${item.key}`)}
                </Link>
              )
            ))}
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;